/** 
 * Pagasus Topics Filter
 * 
 * /!\ This search filter is tightly coupled with the Pegasus Project and shouldn't be "copy/paste" elsewhere
 * 
 * The DOM structure it attach to should be as followed (bawere specific ids and classes)
 *  - a wrapper -> #pegasus-filter-wrapper
 *    - a form -> #pegasus-filter-form
 *      - a keyword input -> #pegasus-filter-keywords-input
 *      - a submit button / link -> .pegasus-filter-submit
 *      - a cancel button / link -> .pegasus-filter-reset
 *    - a result wrapper (which will be show / hide when doing research) -> #pegasus-filter-results-wrapper
 *      - a text element to hold information (i.e. "no result found", "results for keywords", ...) -> #pegasus-filter-keywords
 *      - a result list (where we'll inject results) -> #pegasus-filter-results

 *  - a wrapper for the original content to filter (all li inside it will be considered for filter) -> #pegasus-filter-topics
 *    - a section divider (used to build result's breadcrumbs) -> .pegasus-filter-parent
 *      - a span containing the title for the breadcrumb (we're taking the first span which is a child of .pegasus-filter-parent)
 *    - some lists (potentially nested)
 *      - list elements (this is what we're going to filter) -> :not(.check-all) (filtering out section main checboxes)
 *        - label
 *        - checkbox -> with a unique id which will be used for two ways binding with the original checbox
 *  
 */

class PegasusTopicFilter {
  constructor(wrapper) {
    // Locate form elements
    this.wrapperElement = wrapper;
    this.wrapperElements = document.querySelectorAll('.pegasus-filter-wrapper');
    this.wrapperResultElement = document.querySelector(
      '#pegasus-filter-results-wrapper'
    );
    this.formElement = document.querySelector('#pegasus-filter-form');
    this.inputElement = document.querySelector(
      '#pegasus-filter-keywords-input'
    );
    this.clearElements = document.querySelectorAll('.pegasus-filter-reset');
    this.keywordElement = document.querySelector('#pegasus-filter-keywords');
    this.submitElements = document.querySelectorAll('.pegasus-filter-submit');
    this.resultsElement = document.querySelector('#pegasus-filter-results');
    this.resultTemplateElement = document.querySelector(
      '#pegasus-filter-result-template'
    );

    // get translation from html
    this.translations = {
      noresult:
        this.keywordElement.getAttribute(
          'data-pegasus-filter-keywords-no-results'
        ) || "There's no results for {{keywords}}",
      nokeywords:
        this.keywordElement.getAttribute(
          'data-pegasus-filter-keywords-no-keywords'
        ) || "You didn't entered any keywords",
      results:
        this.keywordElement.getAttribute(
          'data-pegasus-filter-keywords-results'
        ) || 'Results for {{keywords}}',
    };

    // Build a filtrable object from the dom
    // We might improve loading performance by only calling this when the user first type something in the search bar instead of launch
    this.buildTopicsFilterableObject();

    // Display filter form
    this.wrapperElements.forEach((element) =>
      element.classList.remove('nodisplay')
    );
    this.wrapperElements.forEach((element) =>
      element.setAttribute('aria-hidden', false)
    );

    // Hide clear button
    this.clearElementsHide();

    // Set results accessibility attributes (hidden at start)
    this.resultElementHide();

    // OPTIONAL: attach eventHandler on input
    // this.attachKeyEventOnInput();

    // Attach event handlers
    this.attachSubmitEvent();
    this.attachClearEvent();
  }

  /** OPTIONAL: Attach keyup event handler on input to perform autocompletion */
  attachKeyEventOnInput() {}

  /** Attach `submit` event handler on form */
  attachSubmitEvent() {
    this.formElement.addEventListener('submit', this.applyFilter.bind(this));
  }

  /** Attach `click` event handler on 'clear' link */
  attachClearEvent() {
    this.clearElements.forEach((element) =>
      element.addEventListener('click', this.clearResults.bind(this))
    );
  }

  /** Attach events on checkboxes (both original and result for two ways binding) */
  attachCheckboxEventOnResult({ result, original }) {
    result.addEventListener('change', this.checkCheckboxkEventHandler);
    original.addEventListener('change', this.checkCheckboxkEventHandler);
  }

  /**
   * Submit Event Handler
   * Perform search and present results
   */
  applyFilter(event_) {
    event_.preventDefault();

    this.retrieveKeywords.bind(this)();
    this.getRelevantTopics.bind(this)();
    this.displayFilterResults.bind(this)();
  }

  /**
   * Checbox 'change' Event Handler
   * Two way bind the result checkbox and the original checbox
   */
  checkCheckboxkEventHandler(event_) {
    // check which way we have to go and get the counterpart id
    const id = event_.target.id.includes('pegasus-filter-result-')
      ? event_.target.id.slice(22) // result -> original
      : `pegasus-filter-result-${event_.target.id}`; // original -> result

    document.querySelector(`#${id}`).checked = Boolean(event_.target.checked);
  }

  /**
   * Click Event Handler
   * Clear search results
   */
  clearResults(event_) {
    event_.preventDefault();
    this.clearElementsHide(); // hide clear button
    this.resultElementHide(); // hide results
    this.inputElement.value = ''; // empty keyword input
    this.resultsElement.innerHTML = ''; // remove result content
  }

  /**
   * Build a filterable representation of the elements on the page
   * Get topics, each has a `for` attribute with checkbox id and `innerText` attribute with title
   * Turn this into an easy filterable array of {title, id}
   */
  buildTopicsFilterableObject() {
    this.topicsElements = [
      ...document.querySelectorAll(
        '#pegasus-filter-topics li.checkbox-item:not(.check-all) label'
      ),
    ].map((element) => ({
      title: element.innerHTML,
      words: element.innerHTML.split(' ').map((word) => word.toLowerCase()),
      id: element.getAttribute('for'),
      element,
    }));
  }

  /**
   * Get Keywords from search input
   * Keywords are separated by either a space or a comma
   */
  retrieveKeywords() {
    this.keywords = this.inputElement.value
      ?.split(/[ ,]/)
      .filter((keyword) => keyword !== '')
      .map((keyword) => keyword.toLowerCase());
  }

  /** Do the actual filtering */
  getRelevantTopics() {
    this.results = this.topicsElements.filter((topic) => {
      // check if any value from keywords array are also in topic.words array ( https://stackoverflow.com/a/39893636/4530457 )
      // join turns the Array into a String so includes will find partial match (right -> rights, news -> newsletter, ...)
      return this.keywords.some((keyword) =>
        topic.words.join(' ').includes(keyword)
      );
    });
  }

  /** Show Results */
  displayFilterResults() {
    // clear previous results
    this.resultsElement.innerHTML = '';

    // display result wrapper
    this.resultElementShow();

    // show keyword feedback text
    this.showKeywords();

    // show clear button
    this.clearElementsShow();

    // inject results
    this.results.forEach((topic) => {
      const elements = this.buildResultElement(topic);
      this.resultsElement.append(elements.result);
      this.attachCheckboxEventOnResult(elements);
    });
  }

  /** Show keyword feedback line */
  showKeywords() {
    // no keywords
    if (!this.keywords?.length) {
      this.keywordElement.textContent = this.translations.nokeywords;
      return;
    }

    const keywordString = this.keywords.join(', ');

    // no results
    if (!this.results?.length) {
      this.keywordElement.textContent = this.translations.noresult.replace(
        '{{keywords}}',
        keywordString
      );
      return;
    }

    // results
    this.keywordElement.textContent = this.translations.results.replace(
      '{{keywords}}',
      keywordString
    );
  }

  /** Create the dom list element to insert into the results */
  buildResultElement(topic) {
    // find original topic checkbox
    const original = document.querySelector(`#${topic.id}`);

    // Create li and style it
    const li = document.createElement('li');
    li.classList.add('checkbox-item', 'list-input');

    // Create checkbox, set attributes and check it when original is checked
    const input = document.createElement('input');
    input.setAttribute('type', 'checkbox');
    input.setAttribute('id', `pegasus-filter-result-${topic.id}`);
    input.checked = original.checked;

    // Create label and add styles
    const label = document.createElement('label');
    label.classList.add('checkbox-label');
    label.innerHTML = `${this.getBreadcrumbFor(
      topic.element,
      `<strong>${topic.title}</strong>`
    )}`;
    label.setAttribute('for', `pegasus-filter-result-${topic.id}`);

    // construct result dom
    li.append(input);
    li.append(label);

    return { result: li, original };
  }

  /** Recursively build the breadcrumb for the element */
  getBreadcrumbFor(original, path) {
    const parent = original.parentElement?.closest('.pegasus-filter-parent'); // beware that element.closest return itself if element match the critera

    let newPath = path;

    if (parent) {
      const parentSpan = parent.querySelector('span');
      if (parentSpan) {
        newPath = `${parentSpan.textContent} / ${path}`;
        return this.getBreadcrumbFor(parent, newPath);
      }
    }

    return newPath;
  }

  /** Show Result Element with accessibility attributes */
  resultElementShow() {
    this.wrapperResultElement.classList.remove('nodisplay');
    this.wrapperResultElement.setAttribute('aria-hidden', false);
    this.wrapperResultElement.setAttribute('tab-index', 0);
  }

  /** Hide Result Element with accessibility attributes */
  resultElementHide() {
    this.wrapperResultElement.classList.add('nodisplay');
    this.wrapperResultElement.setAttribute('aria-hidden', true);
    this.wrapperResultElement.setAttribute('tab-index', -1);
  }

  /** Show clear button */
  clearElementsShow() {
    this.clearElements.forEach((element) => {
      element.classList.remove('nodisplay');
      element.setAttribute('aria-hidden', false);
      element.setAttribute('tab-index', 0);
    });
  }

  /** Hide clear button */
  clearElementsHide() {
    this.clearElements.forEach((element) => {
      element.classList.add('nodisplay');
      element.setAttribute('aria-hidden', true);
      element.setAttribute('tab-index', -1);
    });
  }

  /** Initialize filter */
  static init() {
    document
      .querySelectorAll('#pegasus-filter-wrapper')
      .forEach((wrapper) => new PegasusTopicFilter(wrapper));
    console.log('Search init -> ok');
  }
}

export default PegasusTopicFilter;
